import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation, ChangeDetectorRef, inject } from '@angular/core';
import { ENTER } from '@angular/cdk/keycodes';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';

import { debounceTime, distinctUntilChanged, filter, Observable } from 'rxjs';
import Quill from 'quill';
import clone from 'lodash-es/clone';
import { MentionConfig } from 'angular-mentions';
import { Actions, ofActionCompleted, Select, Store } from '@ngxs/store';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { User } from '@core/models';
import { Note } from '../notes.types';
import { AccountService } from 'app/core';
import { NotesState } from '../state/state';
import { UserState } from '@state/user/state';
import { NotesActions } from '../state/actions';
import { Module, NewPermission } from '@core/enums';
import { ContactsService } from '@modules/contacts/contacts.service';
import { MentionsService } from '@shared/services/mentions.service';
import { MSWORD_MATCHERS, QUILL_MODULES } from '@shared/Utils/quill-editor.utils';

declare type PanelModeType = 'view' | 'add' | 'edit';

@Component({
  selector: 'note-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NotesDetailsComponent extends OnDestroyMixin implements OnInit {
  note: Note;
  replyFormActive: boolean;
  panelMode: PanelModeType = 'view';
  noteForm: UntypedFormGroup;
  quillModules = QUILL_MODULES;
  public mentionConfig: MentionConfig;
  separatorKeysCodes: number[] = [ENTER];
  contactList: any[] = [];
  selectedUsers: any[] = [];
  userCtrl: UntypedFormControl;
  quill: Quill;
  NewPermission = NewPermission;
  @ViewChild('userInput') userInput: ElementRef<HTMLInputElement>;

  @Select(NotesState.getSelectedNote) selectedNote$: Observable<Note>;

  private get module(): string | undefined {
    return this.routeData?.module;
  }

  private get routeData(): any {
    return this.activatedRoute.snapshot.data;
  }

  readonly mentionsService = inject(MentionsService);

  constructor(
    private readonly elementRef: ElementRef,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly contactsService: ContactsService,
    private readonly accountService: AccountService,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    super();

    this.actions$.pipe(ofActionCompleted(NotesActions.UpdateNote), untilComponentDestroyed(this)).subscribe(() => {
      this.loadNote(this.store.selectSnapshot(NotesState.getSelectedNote).id);
    });

    this.userCtrl = new UntypedFormControl();
    this.replyFormActive = false;
    this.contactsService.accountId = this.accountService.getCurrentAccount()?.id;
  }

  ngOnInit(): void {
    this.noteForm = this.formBuilder.group({
      id: [''],
      title: [''],
      body: [''],
    });

    this.mentionConfig = this.mentionsService.getDefaultMentionConfig();

    this.selectedNote$.pipe(untilComponentDestroyed(this)).subscribe((note: Note) => {
      this.note = note;
      this.selectedUsers = this.note?.contacts || [];
      this.noteForm.patchValue(note, { emitEvent: false });

      if (!note?.title || !note?.body) {
        this.changeEventPanelMode('add');
      } else {
        this.changeEventPanelMode('view');
      }

      this.changeDetectorRef.markForCheck();
    });

    this.userCtrl.valueChanges
      .pipe(filter(Boolean), untilComponentDestroyed(this), debounceTime(400), distinctUntilChanged())
      .subscribe((query: string) => {
        this.contactsService
          .getContacts(1, 20, 'first_name', 'asc', query)
          .pipe(untilComponentDestroyed(this))
          .subscribe((response: any) => {
            this.contactList = response.contacts;
            this.changeDetectorRef.markForCheck();
          });
      });
  }

  /**
   * Get the editor instance and add the MS Word matchers.
   */
  onEditorCreated(quill: Quill): void {
    this.quill = quill;
    const clipboard = this.quill.getModule('clipboard') as any;

    MSWORD_MATCHERS.forEach(([selector, matcher]) => {
      clipboard.addMatcher(selector, matcher);
    });
  }

  loadNote(id: number): void {
    this.store.dispatch(new NotesActions.GetNoteById(id));
  }

  updateNote(): void {
    let note = clone(this.noteForm.value) as Note;
    note.contact_ids = this.selectedUsers.map((user) => user.id);
    note.body = this.mentionsService.transformMentionsForBackend(note.body);
    this.store.dispatch(new NotesActions.UpdateNote(note));
  }

  deleteNote(): void {
    if (this.note && this.note.id) {
      this.store.dispatch(new NotesActions.DeleteNote(this.note.id));
    }
  }

  changeEventPanelMode(panelMode: PanelModeType): void {
    this.panelMode = panelMode;
  }

  reply(): void {
    // Activate the reply form
    this.replyFormActive = true;

    // Scroll to the bottom of the details pane
    setTimeout(() => {
      this.elementRef.nativeElement.scrollTop = this.elementRef.nativeElement.scrollHeight;
    });
  }

  discard(): void {
    // Deactivate the reply form
    this.replyFormActive = false;
  }

  send(): void {
    // Deactivate the reply form
    this.replyFormActive = false;
  }

  removeUser(user: User): void {
    this.selectedUsers.splice(
      this.selectedUsers.findIndex((item) => item.id === user.id),
      1,
    );
    this.changeDetectorRef.markForCheck();
  }

  selectedUser(event: MatAutocompleteSelectedEvent): void {
    this.selectedUsers.push({
      id: event.option.id,
      name: event.option.viewValue,
    });
    if (this.userInput) {
      this.userInput.nativeElement.value = '';
    }
    this.userCtrl.setValue(null);
  }

  getEditPermission(): boolean {
    if (this.module === Module.PROPERTIES) {
      return (
        this.store.selectSnapshot(UserState.hasPermission(NewPermission.PROPERTIES_EDIT)) &&
        this.routeData.property.permissions.is_editable
      );
    } else if (this.module === Module.DEALS) {
      return (
        this.store.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_EDIT)) &&
        this.routeData.project.permissions.is_editable
      );
    }

    return true;
  }
}
